import * as Yup from 'yup';

const PHONE_RE = /^\(\d{3}\) \d{3}-\d{4}$/;
const SSN_RE = /^\d{3}-\d{2}-\d{4}$/;
const ZIP_RE = /^\d{5}$/;
const YEAR_RE = /^\d{4}$/;

// Each helper skips validation when the value is absent / empty.
const phone = () =>
  Yup.string().matches(PHONE_RE, { message: 'Format: (xxx) xxx-xxxx', excludeEmptyString: true });
const ssn = () =>
  Yup.string().matches(SSN_RE, { message: 'Format: xxx-xx-xxxx', excludeEmptyString: true });
const zip = () =>
  Yup.string().matches(ZIP_RE, { message: 'Enter a valid zip code', excludeEmptyString: true });
const year = () =>
  Yup.string().matches(YEAR_RE, { message: 'Enter a 4-digit year', excludeEmptyString: true });
const email = () =>
  Yup.string().test('email-fmt', 'Enter a valid email', (v) =>
    !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
  );
const nameMin = () =>
  Yup.string().test('name-min', 'At least 2 characters', (v) => !v || v.trim().length >= 2);
const birthdate = () =>
  Yup.string().test('birthdate-range', 'Enter a valid birth date', (v) => {
    if (!v) return true;
    const year = Number(v.slice(0, 4));
    const d = new Date(v);
    return !isNaN(d.getTime()) && year >= new Date().getFullYear() - 120 && d.getTime() <= Date.now();
  });
const intRange = (max: number) =>
  Yup.mixed().test(`range-${max}`, `Must be 0–${max}`, (v) => {
    if (v === '' || v === null || v === undefined) return true;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 && n <= max;
  });
const maxLen = (max: number) => Yup.string().max(max, `Must be ${max} characters or less`);
const sigMatchesName = (firstKey: string, lastKey: string, msg: string) =>
  Yup.string().test('sig-match', msg, function (value) {
    if (!value) return true;
    const first: string = (this.parent[firstKey] ?? '') as string;
    const last: string = (this.parent[lastKey] ?? '') as string;
    const expected = `${first} ${last}`.trim().toLowerCase();
    return !expected || value.trim().toLowerCase() === expected;
  });

export function buildAgentServiceFormSchema(_isPhone: boolean) {
  return Yup.object({
    // ── Buyer ──
    firstName: nameMin(),
    lastName: nameMin(),
    emailAddress: email(),
    primaryPhoneNumber: phone(),
    ssn: ssn(),
    birthdate: birthdate(),

    // ── Residential ──
    residentialZipCode: zip(),
    yearsAtResidentialAddress: intRange(99),
    monthsAtResidentialAddress: intRange(11),
    previousYearsAtAddress: intRange(99),
    previousMonthsAtAddress: intRange(11),

    // ── Employment ──
    retirementYears: intRange(99),
    retirementMonths: intRange(11),
    employerName: maxLen(100),
    employerAddressLine1: maxLen(100),
    employerCity: maxLen(100),
    employedYears: intRange(99),
    employedMonths: intRange(11),
    previousEmployerName: maxLen(100),
    previousEmployedYears: intRange(99),
    previousEmployedMonths: intRange(11),

    // ── Co-Buyer ──
    haveCoApplicant: Yup.boolean(),
    coapplicantPrimaryPhoneNumber: phone(),
    coapplicantEmailAddress: email(),
    coapplicantSsn: ssn(),
    coapplicantBirthdate: birthdate(),
    coapplicantResidentialZipCode: zip(),
    coapplicantYearsAtResidentialAddress: intRange(99),
    coapplicantMonthsAtResidentialAddress: intRange(11),
    coapplicantPreviousYearsAtAddress: intRange(99),
    coapplicantPreviousMonthsAtAddress: intRange(11),
    coapplicantRetirementYears: intRange(99),
    coapplicantRetirementMonths: intRange(11),
    coapplicantEmployerName: maxLen(100),
    coapplicantEmployerAddressLine1: maxLen(100),
    coapplicantEmployerCity: maxLen(100),
    coapplicantEmployedYears: intRange(99),
    coapplicantEmployedMonths: intRange(11),
    coapplicantPreviousEmployerName: maxLen(100),
    coapplicantPreviousEmployedYears: intRange(99),
    coapplicantPreviousEmployedMonths: intRange(11),

    // ── Purchase ──
    vehicleYear: year(),

    // ── Partner ──
    partnerRepEmail: email(),
    miscDescription: Yup.string().test('misc-desc-required', 'Required when Misc. Cost is entered', function (v) {
      const cost = String(this.parent.miscCost ?? '').replace(/,/g, '');
      const n = parseFloat(cost);
      return isNaN(n) || n <= 0 || !!v?.trim();
    }),

    // ── Authorization ──
    applicantAgree: Yup.boolean().oneOf([true], 'Please check "I agree"'),
    usResident: Yup.boolean().test(
      'citizen-or-resident',
      'Check "I am a U.S. Citizen" or "I am a Lawful Permanent Resident"',
      function (value) {
        return !!value || !!this.parent.usCitizen;
      },
    ),
    applicantSignature: sigMatchesName('firstName', 'lastName', 'Must match the full name entered above'),

    coApplicantAgree: Yup.boolean().when('haveCoApplicant', {
      is: true,
      then: (schema) => schema.oneOf([true], 'Please check "I agree"'),
      otherwise: (schema) => schema.optional(),
    }),
    coApplicantUsResident: Yup.boolean().when('haveCoApplicant', {
      is: true,
      then: (schema) => schema.test(
        'co-citizen-or-resident',
        'Check "I am a U.S. Citizen" or "I am a Lawful Permanent Resident"',
        function (value) {
          return !!value || !!this.parent.coApplicantUsCitizen;
        },
      ),
      otherwise: (schema) => schema.optional(),
    }),
    coApplicantSignature: sigMatchesName(
      'coapplicantFirstName',
      'coapplicantLastName',
      'Must match the co-buyer name entered above',
    ),
  });
}
